<script setup lang="ts">
import { computed, useSlots } from 'vue';
import type { Component } from 'vue';
import { useI18n } from 'vue-i18n';

import { AlertTriangle, Check, Info, Xcircle } from '@celerdata/icons';
import { Close, Loading } from '@element-plus/icons-vue';

export type CDAlertType = 'info' | 'success' | 'warning' | 'error';
// 'bar' = white surface + left accent bar (v3 default). 'fill' = tinted
// background + full colored border + colored text (legacy InfoBox look).
export type CDAlertVariant = 'bar' | 'fill';

// `fill*` map to the design-token feedback palette (soft bg / *-border / muted
// text), whose light-mode values equal the legacy Vue 2 InfoBox hex 1:1 and
// flip under dark mode — so the fill variant matches legacy without hand-rolled
// hex. info's readable text is `-strong` (base `--cd-info` is too bright).
const TYPE_META: Record<
  CDAlertType,
  {
    accent: string;
    border: string;
    fillBg: string;
    fillBorder: string;
    fillText: string;
    icon: Component;
  }
> = {
  info: {
    accent: 'var(--cd-info)',
    border: 'rgba(0, 97, 255, 0.22)',
    fillBg: 'var(--cd-info-soft)',
    fillBorder: 'var(--cd-info-border)',
    fillText: 'var(--cd-info-strong)',
    icon: Info,
  },
  success: {
    accent: 'var(--cd-success)',
    border: 'rgba(0, 168, 112, 0.22)',
    fillBg: 'var(--cd-success-soft)',
    fillBorder: 'var(--cd-success-border)',
    fillText: 'var(--cd-success)',
    icon: Check,
  },
  warning: {
    accent: 'var(--cd-brand-tangerine)',
    border: 'rgba(246, 134, 70, 0.22)',
    fillBg: 'var(--cd-warning-soft)',
    fillBorder: 'var(--cd-warning-border)',
    fillText: 'var(--cd-warning)',
    icon: AlertTriangle,
  },
  error: {
    accent: 'var(--cd-danger)',
    border: 'rgba(255, 86, 48, 0.22)',
    fillBg: 'var(--cd-danger-soft)',
    fillBorder: 'var(--cd-danger-border)',
    fillText: 'var(--cd-danger)',
    icon: Xcircle,
  },
};

const {
  type = 'info',
  variant = 'bar',
  title = '',
  description = '',
  closable = true,
  showIcon = false,
  icon = undefined,
  pending = false,
} = defineProps<{
  type?: CDAlertType;
  // Visual theme — see CDAlertVariant. Default 'bar' keeps every existing
  // usage unchanged; 'fill' opts into the legacy tinted-background look.
  variant?: CDAlertVariant;
  title?: string;
  description?: string;
  closable?: boolean;
  showIcon?: boolean;
  // Override the per-type icon (e.g. a warning triangle on an error-hued
  // banner) without changing the type's color/role.
  icon?: Component;
  // Close shows a spinner and suppresses the close emit while an async
  // dismissal (e.g. mark-read mutation) is in flight.
  pending?: boolean;
}>();

const emit = defineEmits<{
  close: [];
}>();

const slots = useSlots();
const { t } = useI18n();

const meta = computed(() => TYPE_META[type]);
const isFill = computed(() => variant === 'fill');
const hasTitle = computed(() => !!title || !!slots.title);
const hasDescription = computed(() => !!description || !!slots.default);
const ariaRole = computed(() => (type === 'error' || type === 'warning' ? 'alert' : 'status'));
const ariaLive = computed(() => (ariaRole.value === 'alert' ? 'assertive' : 'polite'));
const alertStyle = computed(() => ({
  '--cd-alert-accent': meta.value.accent,
  '--cd-alert-border': isFill.value ? meta.value.fillBorder : meta.value.border,
  '--cd-alert-fill-bg': meta.value.fillBg,
  '--cd-alert-fill-text': meta.value.fillText,
}));

function onClose(): void {
  if (!pending) emit('close');
}
</script>

<template>
  <div
    class="relative flex gap-3 overflow-hidden rounded-[4px] px-4 py-3 shadow-[inset_0_0_0_1px_var(--cd-alert-border)]"
    :class="[
      hasTitle || showIcon ? 'items-start' : 'items-center',
      isFill
        ? 'bg-[var(--cd-alert-fill-bg)] text-[13px] text-[var(--cd-alert-fill-text)]'
        : 'bg-surface text-fg text-sm',
    ]"
    :style="alertStyle"
    :role="ariaRole"
    :aria-live="ariaLive"
    data-testid="cd-alert"
  >
    <div
      v-if="!isFill"
      class="pointer-events-none absolute inset-y-0 left-0 w-[4px] bg-[var(--cd-alert-accent)]"
    />
    <ElIcon
      v-if="showIcon"
      class="shrink-0"
      :class="isFill ? 'text-[var(--cd-alert-fill-text)]' : 'text-[var(--cd-alert-accent)]'"
      :size="isFill ? 18 : 20"
    >
      <component :is="icon ?? meta.icon" />
    </ElIcon>
    <div class="flex min-w-0 flex-col gap-0.5">
      <div v-if="hasTitle" class="leading-snug font-semibold">
        <slot name="title">{{ title }}</slot>
      </div>
      <div
        v-if="hasDescription"
        class="leading-5"
        :class="[hasTitle ? 'text-xs' : 'text-[13px]', isFill ? '' : 'text-fg-2']"
      >
        <slot>{{ description }}</slot>
      </div>
    </div>
    <!-- Element Plus alert structure: close (and trailing actions) are
         absolutely pinned to the top-right corner, outside the content
         flow — no row inflation, no reserved padding. -->
    <div v-if="closable || slots.actions" class="absolute top-3.5 right-4 flex items-center gap-3">
      <slot name="actions" />
      <button
        v-if="closable"
        type="button"
        class="text-fg-3 hover:text-fg inline-flex items-center justify-center rounded transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
        :class="pending ? 'cursor-wait' : 'cursor-pointer'"
        :aria-label="t('common.close')"
        data-testid="cd-alert-close"
        @click.stop="onClose"
      >
        <ElIcon :size="16" :class="{ 'animate-spin': pending }">
          <component :is="pending ? Loading : Close" />
        </ElIcon>
      </button>
    </div>
  </div>
</template>
